import { useMemo } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ProjectDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  EmptyState,
  FilterPill,
  PageHeader,
  Panel,
  ProgressBar,
  SectionHeader,
  toneBorder,
  toneSubtle,
  type Tone,
} from "@/design/primitives";
import { DataTable, type Column } from "@/design/DataTable";
import { lifecycleLabel, lifecycleTone, projectTone } from "@/design/project-visual";
import { relativeTime, sessionCostLabel } from "@/lib/format";
import { OrbitMap } from "./OrbitMap";
import { openJarvis } from "@/jarvis/jarvis-store";
import { useIsMobile } from "@/lib/useIsMobile";
import { MobileHome } from "@/mobile/MobileHome";
import { ActivityStream } from "./ActivityStream";

/**
 * Command Center — první obrazovka.
 *
 * `/Design` pro ni artboard nemá (35 jich pokrývá rozhodnutí, projekt, task,
 * plán, workery, ekonomiku a formuláře), takže je postavená z jeho slovníku:
 * hlavička jako v p1a, pás dlaždic jako stat strip v p2a a tabulka podle
 * p4c — „které projekty běží, které stojí a na čem, jmenovitě".
 *
 * Orbita zůstává jako druhý pohled: je to signatura aplikace, ale na otázku
 * „co mě dneska brzdí" odpovídá tabulka rychleji.
 */
export function CommandCenterPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileHome />;
  return <DesktopCommandCenter />;
}

function DesktopCommandCenter() {
  const navigate = useNavigate();
  // pohled drží adresa, ne komponenta — odkaz na orbitu jde poslat dál
  const search = useSearch({ from: "/shell/" }) as { view?: "orbit" };
  const view = search.view === "orbit" ? "orbit" : "table";
  const setView = (next: "table" | "orbit") =>
    void navigate({ to: "/", search: next === "orbit" ? { view: "orbit" } : {} });
  const projectsQuery = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const auditToday = useQuery({
    queryKey: ["audit", "today"],
    queryFn: () => api.audit({ limit: 200 }),
  });
  const inbox = useQuery({ queryKey: ["inbox"], queryFn: () => api.decisionInbox() });
  const incidentsQuery = useQuery({
    queryKey: ["incidents"],
    queryFn: api.incidents,
    refetchInterval: 30_000,
  });
  const goalsQuery = useQuery({ queryKey: ["goals"], queryFn: api.goals });
  const workersQuery = useQuery({
    queryKey: ["workers"],
    queryFn: api.workers,
    refetchInterval: 30_000,
  });

  const inboxItems = inbox.data?.items ?? [];
  const incidents = incidentsQuery.data?.incidents ?? [];
  const criticalIncident = incidents.find((i) => i.severity === "P0" || i.severity === "P1") ?? null;
  const workers = workersQuery.data?.workers ?? [];
  const onlineWorkers = workers.filter((w) => w.online);
  const liveSessions = workers.filter((w) => w.activeSession);
  const projects = projectsQuery.data?.projects ?? [];

  const todayCount = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return (auditToday.data?.events ?? []).filter((e) => new Date(e.ts) >= start).length;
  }, [auditToday.data]);

  /** Kolik rozhodnutí čeká na kterém projektu — v tabulce je to „co ho drží". */
  const decisionsByProject = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of inboxItems) {
      if (!item.projectId) continue;
      map.set(item.projectId, (map.get(item.projectId) ?? 0) + 1);
    }
    return map;
  }, [inboxItems]);

  const urgentCount = inboxItems.filter((i) => i.urgency === "URGENT").length;
  // projekt, který nejvíc čeká na člověka — pravý sloupec p9a
  const hotspot = projects.find((p) => p.attentionScore >= 60 && p.lifecycle !== "PAUSED") ?? null;
  const pausedCount = projects.filter((p) => p.lifecycle === "PAUSED").length;
  const readyCount = projects.filter((p) => p.factoryReadyAt).length;

  const columns: Column<ProjectDTO>[] = [
    {
      key: "project",
      label: cs.commandCenter.colProject,
      width: "minmax(160px, 1fr)",
      primary: true,
      cell: (p) => (
        <span className="flex items-center gap-2">
          {p.name}
          {p.lifecycle !== "ACTIVE_DEV" ? (
            <Chip tone={lifecycleTone[p.lifecycle]}>{lifecycleLabel[p.lifecycle]}</Chip>
          ) : null}
        </span>
      ),
    },
    {
      key: "rounds",
      label: cs.commandCenter.colRounds,
      width: "150px",
      // šest teček kaskády: ✓ splněno, ▶ běží, číslo čeká, červené drží brána
      cell: (p) => (
        <span className="af-mono flex gap-1 text-[10px]">
          {p.rounds.map((r) => (
            <span
              key={r.key}
              title={`${r.order}. ${cs.commandCenter.roundState[r.state]}`}
              className="flex h-[18px] w-[18px] items-center justify-center"
              style={{
                borderRadius: "var(--radius-chip)",
                background:
                  r.state === "done"
                    ? "var(--positive-bg)"
                    : r.state === "running"
                      ? "var(--accent-bg)"
                      : r.state === "blocked"
                        ? "var(--critical-bg)"
                        : "var(--surface-inset)",
                color:
                  r.state === "done"
                    ? "var(--positive-on-tint)"
                    : r.state === "running"
                      ? "var(--accent-on-tint)"
                      : r.state === "blocked"
                        ? "var(--critical-on-tint)"
                        : "var(--text-tertiary)",
              }}
            >
              {r.state === "done" ? "✓" : r.state === "running" ? "▶" : r.order}
            </span>
          ))}
        </span>
      ),
    },
    {
      key: "gate",
      label: cs.commandCenter.colGate,
      width: "minmax(200px, 1.4fr)",
      cell: (p) => {
        const hold = describeHold(p, decisionsByProject.get(p.id) ?? 0);
        return <span style={{ color: hold.color }}>{hold.text}</span>;
      },
    },
    {
      key: "attention",
      label: cs.commandCenter.colAttention,
      width: "90px",
      cell: (p) => <Chip tone={projectTone(p)}>{p.attentionScore}</Chip>,
    },
    {
      key: "ready",
      label: cs.commandCenter.colReady,
      width: "200px",
      // ukazatel postupu plánu + odhad; hotový projekt má plný zelený pruh
      cell: (p) => {
        if (p.factoryReadyAt) {
          return (
            <span className="flex items-center gap-2">
              <ReadyBar value={100} done />
              <span className="af-mono text-[10px]" style={{ color: "var(--positive)" }}>
                100 %
              </span>
            </span>
          );
        }
        if (p.planTotal === 0) {
          return (
            <span className="af-mono text-[10px]" style={{ color: "var(--text-tertiary)" }}>
              — {cs.commandCenter.noPlan}
            </span>
          );
        }
        return (
          <span className="flex items-center gap-2">
            <ReadyBar value={(p.planDone / p.planTotal) * 100} />
            <span className="af-mono text-[10px]" style={{ color: "var(--text-tertiary)" }}>
              {p.etaDays != null ? `~${p.etaDays} ${cs.commandCenter.days}` : `${p.planDone}/${p.planTotal}`}
            </span>
          </span>
        );
      },
    },
  ];

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.commandCenter.title}
        kicker="FACTORY"
        lead={
          <>
            {cs.commandCenter.heroRunning}{" "}
            <span style={{ color: inboxItems.length ? "var(--warning)" : "var(--positive)" }}>
              {inboxItems.length
                ? cs.commandCenter.heroDecisions(inboxItems.length)
                : cs.commandCenter.heroNoDecisions}
            </span>
            {criticalIncident ? (
              <>
                {" "}
                <Link
                  to="/incidents/$id"
                  params={{ id: criticalIncident.id }}
                  style={{ color: "var(--critical)" }}
                >
                  {cs.incidents.hero(incidents.length)}
                </Link>
              </>
            ) : null}
          </>
        }
        right={
          <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            {projects.length} {cs.commandCenter.projectsOnline} · {todayCount}{" "}
            {cs.commandCenter.eventsToday} · {cs.commandCenter.audited}
          </span>
        }
      />

      {/* pás dlaždic — týž tvar jako stat strip detailu projektu (p2a) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Tile
          label={cs.commandCenter.activeSessions}
          value={liveSessions.length}
          note={cs.commandCenter.workersOnline(onlineWorkers.length)}
          noteTone={onlineWorkers.length ? "green" : undefined}
        />
        <Tile
          label={cs.commandCenter.decisionInbox}
          value={inboxItems.length}
          tone={inboxItems.length ? "amber" : undefined}
          note={
            urgentCount
              ? cs.commandCenter.urgent(urgentCount)
              : inboxItems.length
                ? cs.commandCenter.noneUrgent
                : cs.commandCenter.nothingWaits
          }
          to="/decisions"
        />
        <Tile
          label={cs.commandCenter.eventsTodayLabel}
          value={todayCount}
          note={cs.commandCenter.audited}
          to="/audit"
        />
        <Tile
          label={cs.commandCenter.incidents}
          value={incidents.length}
          tone={incidents.length ? "red" : undefined}
          note={criticalIncident ? criticalIncident.label : cs.commandCenter.noIncidents}
        />
        <Tile
          label={cs.commandCenter.projects}
          value={projects.length}
          note={pausedCount ? cs.commandCenter.pausedCount(pausedCount) : cs.commandCenter.allRunning}
          to="/projects"
        />
        <Tile
          label={cs.commandCenter.factoryReady}
          value={`${readyCount}/${projects.length}`}
          tone="green"
          note={cs.commandCenter.factoryReadyNote}
        />
      </div>

      {criticalIncident ? (
        <Link
          to="/incidents/$id"
          params={{ id: criticalIncident.id }}
          className="mt-3 flex flex-wrap items-center gap-3.5 px-[18px] py-3.5"
          style={{
            borderRadius: "var(--radius-card)",
            border: "1px solid color-mix(in srgb, var(--critical) 45%, transparent)",
            background: "color-mix(in srgb, var(--critical) 7%, transparent)",
          }}
          data-testid="cc-incident-banner"
        >
          <span className="af-mono text-[10px] tracking-[0.16em]" style={{ color: "var(--critical)" }}>
            ATTENTION // INCIDENT {criticalIncident.severity}
          </span>
          <span className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
            {criticalIncident.label} — {criticalIncident.title}
          </span>
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {criticalIncident.projectName} · {cs.incidents.minutes(criticalIncident.durationMinutes)}
          </span>
          <span className="ml-auto">
            <Btn variant="secondary" tone="red">
              {cs.commandCenter.solveIncident} →
            </Btn>
          </span>
        </Link>
      ) : null}

      <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-4">
          {/* automatika napříč Factory (p4c) */}
          <Panel className="p-4" data-testid="cc-automation">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionHeader
                label={cs.commandCenter.automation}
                sub={cs.commandCenter.automationScope}
              />
              <div className="flex gap-2">
                <FilterPill active={view === "table"} onClick={() => setView("table")}>
                  {cs.commandCenter.viewTable}
                </FilterPill>
                <FilterPill active={view === "orbit"} onClick={() => setView("orbit")}>
                  {cs.commandCenter.viewOrbit}
                </FilterPill>
              </div>
            </div>
            <div className="mt-1 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
              {cs.commandCenter.automationNote}
            </div>

            {projects.length === 0 ? (
              <EmptyState title={cs.projects.emptyTitle} note={cs.projects.emptyBody} />
            ) : view === "table" ? (
              <div className="mt-4">
                <DataTable
                  columns={columns}
                  rows={projects}
                  rowKey={(p) => p.id}
                  rowTestId={(p) => `cc-project-${p.slug}`}
                  onRowClick={(p) => void navigate({ to: "/projects/$slug", params: { slug: p.slug } })}
                  stackBelow={1024}
                />
                <div
                  className="af-mono mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px]"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  <span>
                    <span style={{ color: "var(--positive)" }}>★</span> {cs.commandCenter.legendReady}
                  </span>
                  <span>
                    <span style={{ color: "var(--critical)" }}>⛔</span>{" "}
                    {cs.commandCenter.legendBlocked}
                  </span>
                  <span>
                    <span style={{ color: "var(--warning)" }}>⏳</span>{" "}
                    {cs.commandCenter.legendWaiting}
                  </span>
                  <span>
                    <span style={{ color: "var(--accent)" }}>▶</span> {cs.commandCenter.legendRunning}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <OrbitMap
                  projects={projects}
                  activitySummary={{ activeSessions: liveSessions.length, tasksToday: todayCount }}
                />
                <div className="flex justify-center pb-2">
                  <Btn variant="secondary" onClick={() => openJarvis()}>
                    ⣿ {cs.commandCenter.talkToJarvis}
                  </Btn>
                </div>
              </>
            )}
          </Panel>

          {/* rozhodnutí — jediné místo, kde se od člověka něco chce */}
          <Panel
            tone={inboxItems.length ? "amber" : undefined}
            className="p-4"
            data-testid="decision-inbox-panel"
          >
            <SectionHeader
              label={cs.commandCenter.decisionInbox}
              tone={inboxItems.length ? "amber" : "cyan"}
              right={<Link to="/decisions">{cs.common.all} →</Link>}
            />
            {inboxItems.length === 0 ? (
              <EmptyState title={cs.commandCenter.heroNoDecisions} />
            ) : (
              <div className="mt-3 flex flex-col gap-2">
                {inboxItems.slice(0, 4).map((item) => (
                  <Link
                    key={item.id}
                    to="/decisions"
                    className="block px-4 py-3"
                    style={{
                      borderRadius: "0 var(--radius-control) var(--radius-control) 0",
                      borderLeft: `3px solid ${item.urgency === "URGENT" ? "var(--critical)" : item.urgency === "HIGH" ? "var(--warning)" : "var(--accent)"}`,
                      background:
                        item.urgency === "URGENT"
                          ? "var(--critical-subtle)"
                          : item.urgency === "HIGH"
                            ? "var(--warning-subtle)"
                            : "var(--accent-subtle)",
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0">
                        <span
                          className="text-[13px] font-semibold"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {item.title}
                        </span>
                        <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                          {" · "}
                          {item.projectName ?? "Factory"}
                          {item.blocksSummary ? ` · ${item.blocksSummary}` : ""}
                        </span>
                      </span>
                      <span
                        className="af-mono shrink-0 text-[10px]"
                        style={{ color: "var(--text-tertiary)" }}
                      >
                        {relativeTime(item.createdAt)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* pravý sloupec */}
        <div className="flex flex-col gap-4">
          {hotspot ? (
            <Panel tone="amber" className="p-[18px]" data-testid="attention-card">
              <div className="flex items-baseline justify-between gap-2">
                <span className="af-label" style={{ color: "var(--warning)" }}>
                  {cs.commandCenter.attentionHotspot}
                </span>
                <Chip tone={projectTone(hotspot)}>{hotspot.attentionScore}</Chip>
              </div>
              <div className="af-panel-title mt-2">{hotspot.name}</div>
              {hotspot.statusLine ? (
                <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {hotspot.statusLine}
                </p>
              ) : null}
              <div className="mt-3">
                <Btn
                  variant="primary"
                  onClick={() => void navigate({ to: "/projects/$slug", params: { slug: hotspot.slug } })}
                >
                  {cs.commandCenter.decide2} →
                </Btn>
              </div>
            </Panel>
          ) : null}

          <Panel className="p-4" data-testid="live-workers-panel">
            <SectionHeader
              label={cs.commandCenter.liveWorkers}
              right={
                <Link
                  to="/workers"
                  style={{ color: onlineWorkers.length ? "var(--positive)" : "var(--text-tertiary)" }}
                >
                  ● {onlineWorkers.length} ONLINE
                </Link>
              }
            />
            {liveSessions.length === 0 ? (
              <EmptyState
                title={workers.length === 0 ? cs.commandCenter.noWorker : cs.commandCenter.workersIdle}
                note={workers.length === 0 ? cs.commandCenter.noWorkerNote : undefined}
              />
            ) : (
              <div className="mt-3 flex flex-col gap-2.5">
                {liveSessions.map((w) => {
                  const s = w.activeSession!;
                  return (
                    <Link
                      key={w.id}
                      to="/sessions/$id"
                      params={{ id: s.id }}
                      className="block px-4 py-3"
                      style={{
                        borderRadius: "var(--radius-box)",
                        border: "1px solid var(--positive-border)",
                        background: "var(--positive-subtle)",
                      }}
                      data-testid="live-session-card"
                    >
                      <div className="flex justify-between text-[13px]">
                        <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
                          {w.name}
                        </span>
                        <span className="af-mono text-[10px]" style={{ color: "var(--text-tertiary)" }}>
                          {s.departmentName ?? ""}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[12px]" style={{ color: "var(--accent)" }}>
                        {s.projectName} → #{s.taskNumber} {s.taskTitle}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <ProgressBar value={s.progress} tone="green" className="flex-1" />
                        <span className="af-mono text-[10px]" style={{ color: "var(--positive)" }}>
                          {s.progress} %
                        </span>
                      </div>
                      <div className="mt-1 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                        {s.currentActivity || cs.commandCenter.working} · {relativeTime(s.startedAt)}
                        {sessionCostLabel(s.weekPctSpent, s.tokensTotal)
                          ? ` · ${sessionCostLabel(s.weekPctSpent, s.tokensTotal)}`
                          : ""}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Panel>

          <ActivityStream />

          <Panel className="p-4" data-testid="cc-goals">
            <SectionHeader label={cs.commandCenter.goals} right={<Link to="/goals">→</Link>} />
            {(goalsQuery.data?.goals ?? []).length === 0 ? (
              <EmptyState title={cs.commandCenter.noGoals} />
            ) : (
              <div className="mt-3 flex flex-col gap-2.5">
                {(goalsQuery.data?.goals ?? []).slice(0, 3).map((g) => (
                  <div key={g.id}>
                    <div className="flex justify-between text-[12px]">
                      <span style={{ color: "var(--text-secondary)" }}>{g.title}</span>
                      <span className="af-mono" style={{ color: "var(--accent)" }}>
                        {g.progress} %
                      </span>
                    </div>
                    <ProgressBar value={g.progress} height={3} className="mt-1.5" />
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <div className="flex justify-end">
            <Btn variant="secondary" onClick={() => openJarvis()} data-testid="cc-talk-jarvis">
              ⣿ {cs.commandCenter.talkToJarvis}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Ukazatel postupu k factory-ready — 110 px pruh z p9a. */
function ReadyBar({ value, done = false }: { value: number; done?: boolean }) {
  return (
    <span
      className="inline-block h-1.5 w-[110px] shrink-0 align-middle"
      style={{ borderRadius: 3, background: "var(--surface-inset)" }}
    >
      <span
        className="block h-1.5"
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
          borderRadius: 3,
          background: done ? "var(--positive)" : "var(--accent-gradient)",
        }}
      />
    </span>
  );
}

/** Dlaždice pásu — týž tvar jako buňka stat stripu projektu. */
function Tile({
  label,
  value,
  note,
  noteTone,
  tone,
  to,
}: {
  label: string;
  value: number | string;
  note?: string;
  /** Barevná poznámka pod číslem — v návrhu je zelená u „workeři online". */
  noteTone?: Tone;
  tone?: Tone;
  to?: string;
}) {
  const tinted = tone && tone !== "dim";
  const body = (
    <div
      className="h-full px-4 py-3"
      style={{
        borderRadius: "var(--radius-box)",
        border: `1px solid ${tinted ? toneBorder(tone) : "var(--border-default)"}`,
        background: tinted ? toneSubtle(tone) : "var(--surface)",
      }}
    >
      <div
        className="af-mono text-[9px] uppercase tracking-[0.14em]"
        style={{ color: tinted ? toneVar(tone) : "var(--text-tertiary)" }}
      >
        {label}
        {to ? " →" : ""}
      </div>
      {/* hodnota drží barvu textu — tón nese karta, ne číslo (p9a) */}
      <div
        className="af-display my-[3px] text-[24px] font-bold leading-none"
        style={{ color: "var(--text-primary)" }}
      >
        {value}
      </div>
      {note ? (
        <div className="text-[10px]" style={{ color: noteTone ? toneVar(noteTone) : "var(--text-tertiary)" }}>
          {note}
        </div>
      ) : null}
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

function toneVar(tone: Tone): string {
  const map: Record<Tone, string> = {
    cyan: "var(--accent)",
    green: "var(--positive)",
    amber: "var(--warning)",
    red: "var(--critical)",
    violet: "var(--info)",
    dim: "var(--text-secondary)",
  };
  return map[tone];
}

/**
 * Co projekt drží. Pořadí je pořadím naléhavosti: pozastavený projekt
 * nezajímá, že má rozhodnutí, a vypnutý modul KANBAN je tichá past —
 * bez desky nemá kickoff kam založit task, takže projekt tiše stojí.
 */
function describeHold(project: ProjectDTO, openDecisions: number): { text: string; color: string } {
  if (project.lifecycle === "PAUSED") {
    return { text: `⏸ ${cs.commandCenter.holdPaused}`, color: "var(--text-tertiary)" };
  }
  if (project.lifecycle === "ARCHIVED") {
    return { text: cs.commandCenter.holdArchived, color: "var(--text-tertiary)" };
  }
  if (openDecisions > 0) {
    return { text: `⛔ ${cs.commandCenter.holdDecisions(openDecisions)}`, color: "var(--critical)" };
  }
  if (project.factoryReadyAt) {
    return { text: `★ ${cs.commandCenter.holdReady}`, color: "var(--positive)" };
  }
  if (project.runningTask) {
    return {
      text: `▶ #${project.runningTask.number} ${project.runningTask.title}`,
      color: "var(--accent)",
    };
  }
  return {
    text: `⏳ ${project.statusLine ?? cs.commandCenter.holdIdle}`,
    color: "var(--text-tertiary)",
  };
}
